<template lang="pug">
  .lh-newsletter-subscription-form__wrapper(:style="cssStyles")
    .lh-newsletter-subscription-form(
      :class="formWrapperClass"
      data-jest="form"
    )
      .lh-newsletter-subscription-form__content-wrapper(:class="contentWrapperClass")
        .lh-newsletter-subscription-form__content
          div.lh-newsletter-subscription-form__text(
            data-jest="text"
            v-if="showTitle"
          )
            slot(name='title')

          div.lh-newsletter-subscription-form__subtitle(v-if="showSubtitle")
            slot(name='subtitle')

          form.lh-newsletter-subscription-form__container(
            @submit.prevent="onSubmit"
          )
            input.lh-newsletter-subscription-form__input(
              :class="inputClasses"
              data-jest="input"
              :disabled="isSending"
              :placeholder="placeholder"
              @input="onInput"
              :value="modelValue"
            )
            button.lh-newsletter-subscription-form__btn(
              :disabled="!Boolean(modelValue) || isSending || !agreementChecked"
              data-jest="submit"
              type="submit"
            ) {{submitButtonText}}
          .lh-newsletter-subscription-form__agreement
            CheckboxInput(
              :id="`agreement-checkbox-${_uid}`"
              v-model="agreementChecked"
            )
              label(:for='`agreement-checkbox-${_uid}`') Даю согласие на <a class="lh-newsletter-subscription-form__link" href="/privacy/" target="_blank">обработку персональных данных</a>
        picture.lh-newsletter-subscription-form__img-wrapper
          img.lh-newsletter-subscription-form__img(
            :src="img"
            data-jest="image"
            loading="lazy"
            alt="Логотип"
          )
      .lh-newsletter-subscription-form__feedback(
        :class="feedbackWrapperClass"
        data-jest="feedback"
      )
        p.lh-newsletter-subscription-form__feedback-title(
          data-jest="feedback-title"
        ) {{feedbackTitle}}
        p.lh-newsletter-subscription-form__feedback-text(
          data-jest="feedback-text"
        ) {{feedback}}
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import CheckboxInput from '../CheckboxInput/CheckboxInput.vue';

  export default defineNuxtComponent({
    name: 'NewsletterSubscriptionForm',

    components: { CheckboxInput },

    props: {
      img: {
        type: String as PropType<string>,
        required: true,
      },
      placeholder: {
        type: String as PropType<string>,
        default: 'Введите почту',
      },
      submitButtonText: {
        type: String as PropType<string>,
        default: 'Подписаться',
      },
      feedbackTitle: {
        type: String as PropType<string>,
        required: true,
      },
      feedback: {
        type: String as PropType<string>,
        required: true,
      },
      modelValue: {
        type: String as PropType<string>,
        required: true,
      },
      contentWrapperColor: {
        type: String as PropType<string>,
        required: true,
      },
      btnColor: {
        type: String as PropType<string>,
        required: true,
      },
      inputBorderActiveColor: {
        type: String as PropType<string>,
        required: true,
      },
      textColor: {
        type: String as PropType<string>,
        required: true,
      },
      darkTheme: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
      location: {
        type: String as PropType<string>,
        default: '',
      },
      /**
       * 'initial' | 'sending' | 'success' | 'failure'
       */
      state: {
        type: String as PropType<'initial' | 'sending' | 'success' | 'failure'>,
        required: true,
      },
    },

    data(): Record<string, boolean> {
      return {
        valid: true,
        agreementChecked: false,
      };
    },

    computed: {
      formWrapperClass(): any {
        return {
          [`lh-newsletter-subscription-form__wrapper--${this.location}`]: this.location ?? null,
        };
      },
      contentWrapperClass(): any {
        return {
          'lh-newsletter-subscription-form__content-wrapper--hidden': !this.showForm,
        };
      },
      feedbackWrapperClass(): any {
        return {
          'lh-newsletter-subscription-form__feedback--hidden': !this.showFeedback,
        };
      },
      cssStyles(): Record<string, string> {
        return {
          '--lh-wrapper-color': this.darkTheme ? '#1E292F' : this.contentWrapperColor,
          '--lh-btn-color': this.darkTheme ? '#414B50' : this.btnColor,
          '--lh-btn-color-disabled': this.darkTheme ? '#414B50' : `${this.btnColor}4D`,
          '--lh-text-color': this.darkTheme ? '#FFFFFF' : this.textColor,
          '--lh-input-border-active-color': this.darkTheme
            ? '#414B50'
            : this.inputBorderActiveColor,
          '--lh-input-bg-color': this.darkTheme ? '#1E292F' : '#fff',
          '--lh-input-border-color': this.darkTheme ? '#304047' : '#e7e7e7',
          '--lh-input-text-color': this.darkTheme ? '#fff' : '#000',
        };
      },
      checkValid(): boolean {
        const emailPattern = /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/;
        return !!this.modelValue && emailPattern.test(this.modelValue);
      },
      inputClasses(): Record<string, boolean> {
        return {
          'lh-newsletter-subscription-form__input_valid':
            Boolean(this.modelValue) && this.valid && this.state === 'initial',
          'lh-newsletter-subscription-form__input_invalid': Boolean(this.modelValue) && !this.valid,
        };
      },
      showForm(): boolean {
        return ['initial', 'sending'].includes(this.state);
      },
      showFeedback(): boolean {
        return ['success', 'failure'].includes(this.state);
      },
      isSending(): boolean {
        return this.state === 'sending';
      },
      showTitle(): boolean {
        return !!this.$slots.title;
      },
      showSubtitle(): boolean {
        return !!this.$slots.subtitle;
      },
    },

    methods: {
      onInput(event: InputEvent): void {
        this.valid = true;
        this.$emit('update:modelValue', (event.target as HTMLInputElement).value);
      },
      onSubmit(): void {
        if (this.checkValid) {
          this.$emit('form-submit');
        } else {
          this.valid = false;
        }
      },
    },
  });
</script>

<style lang="scss" src="./NewsletterSubscriptionForm.scss"></style>
